import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import useAuthBlob from '../hooks/useAuthBlob';

export function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isImage(mime) { return mime && mime.startsWith('image/'); }
export function isVideo(mime) { return mime && mime.startsWith('video/'); }

function LightboxSlide({ item, apiUrl, token }) {
  const fileName = item.fileName || item.file_name;
  const mime = item.mime || item.file_mime;
  const needsFetch = !item.blobUrl;
  const fullUrl = needsFetch && item.file_url
    ? (item.file_url.startsWith('http') ? item.file_url : `${apiUrl?.replace('/api', '')}${item.file_url}`)
    : null;
  const { blobUrl: fetchedBlobUrl, error } = useAuthBlob(fullUrl, token, needsFetch);
  const blobUrl = item.blobUrl || fetchedBlobUrl;

  if (error) {
    return <div className="flex h-40 items-center justify-center text-sm text-white/60">Unavailable</div>;
  }
  if (!blobUrl) {
    return (
      <div className="flex h-40 items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-white/60" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }
  if (isVideo(mime)) {
    return (
      <video
        src={blobUrl}
        controls
        playsInline
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] max-w-[90vw] rounded-xl object-contain shadow-2xl sm:max-h-[90vh]"
      />
    );
  }
  return (
    <img
      src={blobUrl}
      alt={fileName || 'image'}
      onClick={(e) => e.stopPropagation()}
      className="max-h-[80vh] max-w-[90vw] rounded-xl object-contain shadow-2xl sm:max-h-[90vh]"
    />
  );
}

function touchDistance(touches) {
  const [a, b] = touches;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// Wraps a slide with a fade + slight horizontal slide-in whenever `dir` changes,
// so navigating between photos feels like motion instead of a hard cut.
function AnimatedSlide({ children, dir }) {
  const [entered, setEntered] = useState(false);
  // key={index} on the caller remounts this per-slide, so an empty dep array
  // runs the entrance exactly once per slide instead of replaying on drag/zoom re-renders.
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      style={{
        transform: entered ? 'translateX(0)' : `translateX(${dir * 32}px)`,
        opacity: entered ? 1 : 0,
        transition: 'transform 0.28s cubic-bezier(0.22,0.61,0.36,1), opacity 0.22s ease',
      }}
    >
      {children}
    </div>
  );
}

export function LightboxPortal({ items, index, onClose, onNavigate, apiUrl, token }) {
  const item = items[index];
  const hasMultiple = items.length > 1;
  const mime = item?.mime || item?.file_mime;
  const zoomable = isImage(mime);

  // Swipe left/right to navigate, swipe down to close (only while not zoomed in) —
  // shared between touch drag and mouse click-drag via startInteraction/moveInteraction/endInteraction.
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const dragAxis = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const mouseActive = useRef(false);
  const didDrag = useRef(false);
  const navDir = useRef(0);

  // Pinch / double-tap / wheel zoom, and pan while zoomed in (images only)
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const pinchRef = useRef(null);
  const panRef = useRef(null);
  const lastTapRef = useRef(0);

  const goTo = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= items.length) return;
    navDir.current = newIndex > index ? 1 : -1;
    onNavigate?.(newIndex);
  }, [items.length, onNavigate, index]);

  useEffect(() => {
    setScale(1);
    setPanX(0);
    setPanY(0);
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goTo(index - 1);
      else if (e.key === 'ArrowRight') goTo(index + 1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goTo, index]);

  // Attached natively (not via JSX onWheel) because React marks wheel listeners
  // passive by default, which would silently ignore preventDefault() here.
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !zoomable) return;
    const handler = (e) => {
      e.preventDefault();
      setScale((s) => {
        const next = Math.min(4, Math.max(1, s - e.deltaY * 0.0025 * s));
        if (next <= 1.02) { setPanX(0); setPanY(0); return 1; }
        return next;
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [zoomable]);

  if (!item) return null;

  const resetZoom = () => { setScale(1); setPanX(0); setPanY(0); };

  const startInteraction = (x, y) => {
    didDrag.current = false;
    if (scale > 1) {
      panRef.current = { startX: x, startY: y, startPanX: panX, startPanY: panY };
      return;
    }
    touchStartX.current = x;
    touchStartY.current = y;
    dragAxis.current = null;
    setDragging(true);
  };
  const moveInteraction = (x, y) => {
    if (panRef.current) {
      const dx = x - panRef.current.startX;
      const dy = y - panRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
      setPanX(panRef.current.startPanX + dx);
      setPanY(panRef.current.startPanY + dy);
      return;
    }
    if (touchStartX.current === null) return;
    const dx = x - touchStartX.current;
    const dy = y - touchStartY.current;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
    if (!dragAxis.current && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      dragAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (dragAxis.current === 'x') setDragX(dx);
    else if (dragAxis.current === 'y' && dy > 0) setDragY(dy);
  };
  const endInteraction = () => {
    if (panRef.current) { panRef.current = null; return; }
    const dx = dragX;
    const dy = dragY;
    const axis = dragAxis.current;
    setDragging(false);
    setDragX(0);
    setDragY(0);
    touchStartX.current = null;
    touchStartY.current = null;
    dragAxis.current = null;

    if (axis === 'y' && dy > 90) { onClose(); return; }
    if (axis === 'x' && Math.abs(dx) > 60) goTo(dx < 0 ? index + 1 : index - 1);
  };

  const onTouchStart = (e) => {
    if (zoomable && e.touches.length === 2) {
      pinchRef.current = { startDist: touchDistance(e.touches), startScale: scale };
      return;
    }
    if (e.touches.length !== 1) return;

    if (zoomable) {
      const now = Date.now();
      const isDoubleTap = now - lastTapRef.current < 300;
      lastTapRef.current = isDoubleTap ? 0 : now;
      if (isDoubleTap) {
        if (scale > 1) resetZoom();
        else { setScale(2.5); setPanX(0); setPanY(0); }
        return;
      }
    }
    startInteraction(e.touches[0].clientX, e.touches[0].clientY);
  };
  const onTouchMove = (e) => {
    if (pinchRef.current && e.touches.length === 2) {
      const dist = touchDistance(e.touches);
      const next = Math.min(4, Math.max(1, pinchRef.current.startScale * (dist / pinchRef.current.startDist)));
      setScale(next);
      return;
    }
    if (e.touches.length !== 1) return;
    moveInteraction(e.touches[0].clientX, e.touches[0].clientY);
  };
  const onTouchEnd = () => {
    if (pinchRef.current) {
      pinchRef.current = null;
      if (scale <= 1.05) resetZoom();
      return;
    }
    endInteraction();
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    mouseActive.current = true;
    startInteraction(e.clientX, e.clientY);
  };
  const onMouseMove = (e) => {
    if (!mouseActive.current) return;
    moveInteraction(e.clientX, e.clientY);
  };
  const endMouse = () => {
    if (!mouseActive.current) return;
    mouseActive.current = false;
    endInteraction();
  };

  const onContainerClick = () => {
    if (didDrag.current) { didDrag.current = false; return; }
    onClose();
  };

  const onDoubleClick = () => {
    if (!zoomable) return;
    if (scale > 1) resetZoom();
    else { setScale(2.5); setPanX(0); setPanY(0); }
  };

  const fileName = item.fileName || item.file_name;
  const closeProgress = dragAxis.current === 'y' ? Math.min(1, dragY / 300) : 0;

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] flex select-none items-center justify-center"
      style={{ touchAction: 'none', backgroundColor: `rgba(0,0,0,${(0.9 - closeProgress * 0.5).toFixed(2)})` }}
      onClick={onContainerClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={endMouse}
      onMouseLeave={endMouse}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute z-10 rounded-full bg-black/50 p-3 text-white transition hover:bg-white/20"
        style={{ top: 'max(0.75rem, env(safe-area-inset-top))', right: 'max(0.75rem, env(safe-area-inset-right))' }}
      >
        <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Filename */}
      {fileName && (
        <p
          className="pointer-events-none absolute left-1/2 max-w-[50vw] -translate-x-1/2 truncate text-sm text-white/60"
          style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          {fileName}
        </p>
      )}

      {/* Prev / Next */}
      {hasMultiple && index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
          aria-label="Previous"
          className="absolute left-1 z-10 rounded-full bg-black/50 p-2 text-white transition hover:bg-white/20 hover:scale-110 active:scale-95 sm:left-4 sm:p-3"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      {hasMultiple && index < items.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
          aria-label="Next"
          className="absolute right-1 z-10 rounded-full bg-black/50 p-2 text-white transition hover:bg-white/20 hover:scale-110 active:scale-95 sm:right-4 sm:p-3"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <AnimatedSlide key={index} dir={navDir.current}>
        <div
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={onDoubleClick}
          style={{
            transform: `translate(${panX + dragX}px, ${panY + dragY}px) scale(${(scale * (1 - closeProgress * 0.15)).toFixed(3)})`,
            transition: (dragging || panRef.current || pinchRef.current) ? 'none' : 'transform 0.2s ease',
            touchAction: 'none',
            cursor: zoomable ? (scale > 1 ? 'grab' : 'zoom-in') : 'default',
          }}
        >
          <LightboxSlide item={item} apiUrl={apiUrl} token={token} />
        </div>
      </AnimatedSlide>

      {/* Position counter */}
      {hasMultiple && (
        <div
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white/80"
          style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          {index + 1} / {items.length}
        </div>
      )}
    </div>,
    document.body
  );
}

export default function FileAttachment({ fileUrl, fileName, fileSize, fileMime, apiUrl, token }) {
  const [downloading, setDownloading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const fullUrl = fileUrl
    ? fileUrl.startsWith('http')
      ? fileUrl
      : `${apiUrl?.replace('/api', '')}${fileUrl}`
    : null;

  const needsBlob = isImage(fileMime) || isVideo(fileMime);
  const { blobUrl, error: blobError } = useAuthBlob(fullUrl, token, needsBlob);

  const handleDownload = async () => {
    if (!fullUrl) return;
    setDownloading(true);
    try {
      const res = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'file';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  if (!fullUrl) return <span className="text-slate-400 text-xs">File unavailable</span>;

  if (isImage(fileMime)) {
    if (blobError) return <span className="text-slate-400 text-xs">Image unavailable</span>;
    if (!blobUrl) {
      return (
        <div className="flex items-center justify-center rounded-xl bg-white/10" style={{ width: 200, height: 120 }}>
          <svg className="h-5 w-5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      );
    }
    return (
      <>
        {lightboxOpen && (
          <LightboxPortal
            items={[{ blobUrl, fileName, mime: fileMime }]}
            index={0}
            onClose={() => setLightboxOpen(false)}
          />
        )}
        <div
          onClick={() => setLightboxOpen(true)}
          className="cursor-zoom-in"
        >
          <img
            src={blobUrl}
            alt={fileName || 'image'}
            className="rounded-xl object-cover"
            style={{ maxWidth: 240, maxHeight: 240 }}
          />
          {fileName && <p className="mt-1 text-xs opacity-60 truncate max-w-[240px]">{fileName}</p>}
        </div>
      </>
    );
  }

  if (isVideo(fileMime)) {
    if (!blobUrl && !blobError) {
      return (
        <div className="flex items-center justify-center rounded-xl bg-white/10" style={{ width: 240, height: 140 }}>
          <svg className="h-5 w-5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      );
    }
    return (
      <div>
        <video
          src={blobUrl || undefined}
          controls
          className="rounded-xl"
          style={{ maxWidth: 280, maxHeight: 200 }}
        />
        {fileName && <p className="mt-1 text-xs opacity-60 truncate max-w-[280px]">{fileName}</p>}
      </div>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-3 py-2.5 text-left transition hover:bg-white/20 disabled:opacity-60"
    >
      <svg className="h-8 w-8 flex-shrink-0 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium max-w-[160px]">{fileName || 'File'}</p>
        <p className="text-xs opacity-60">{formatBytes(fileSize)}</p>
      </div>
      <svg className="h-4 w-4 flex-shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
      </svg>
    </button>
  );
}
